import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SEO, SEO_CONFIG } from '@/components/ui/SEO';
import {
  Check,
  X,
  ArrowRight,
  Users,
  Building2,
  Sparkles,
  Zap,
  Shield,
  ChevronDown,
  Star,
  Gift,
  CreditCard,
  Clock,
  RefreshCw,
  Headphones,
  Lock,
  Bot,
  Brain,
  Gem,
  Bolt,
  FlaskConical,
  ClipboardCheck,
  Crown
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { PricingBackground } from '@/components/ui/PricingBackground';

const tierIcons = [Sparkles, Zap, Users, Building2];
const tierGradients = [
  'from-slate-500 to-zinc-600',
  'from-purple-500 to-fuchsia-500',
  'from-purple-500 to-violet-500',
  'from-amber-500 to-orange-500'
];

const aiModels = [
  { name: 'GPT-4.5 Turbo', icon: Bot, color: 'from-green-500 to-emerald-600' },
  { name: 'Claude Opus 4.5', icon: Brain, color: 'from-orange-500 to-amber-600' },
  { name: 'Gemini 2.5 Pro', icon: Gem, color: 'from-blue-500 to-indigo-600' },
  { name: 'Grok 3', icon: Bolt, color: 'from-purple-500 to-violet-600' },
];

export function PricingPage() {
  const { t } = useTranslation();
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [hoveredPlan, setHoveredPlan] = useState<number | null>(null);

  const tierKeys = ['hobby', 'pro', 'team', 'enterprise'] as const;

  const pricingTiers = tierKeys.map((key, index) => ({
    key,
    name: t(`pricing.tiers.${key}.name`),
    price: key === 'hobby' ? 0 : key === 'pro' ? 20 : key === 'team' ? 40 : 'Custom',
    originalPrice: key === 'pro' ? 25 : key === 'team' ? 50 : undefined,
    period: key === 'hobby' ? t('pricing.forever') : key === 'enterprise' ? undefined : key === 'team' ? t('pricing.perUserMonth') : t('pricing.perMonth'),
    description: t(`pricing.tiers.${key}.description`),
    icon: tierIcons[index],
    gradient: tierGradients[index],
    features: key === 'hobby'
      ? ['2,000 completions/month', '50 AI agent requests/month', 'GPT-4.5 Turbo access', 'Community support', 'Basic code analysis']
      : key === 'pro'
      ? ['Unlimited completions', '500 AI agent requests/month', 'All AI models included', 'Priority support', 'Custom AI rules', 'Built-in Unit Testing']
      : key === 'team'
      ? ['Everything in Pro', 'Centralized billing', 'Usage analytics dashboard', 'Admin controls', 'SAML/OIDC SSO', 'Built-in UAT Testing']
      : ['Everything in Team', 'Unlimited AI requests', 'Dedicated success manager', 'Custom contracts & SLA', 'On-premise deployment', 'Custom AI model training'],
    cta: t(`pricing.tiers.${key}.cta`),
    ctaLink: key === 'enterprise' ? '/contact' : '/download',
    popular: key === 'pro'
  }));

  const comparisonFeatures = [
    { key: 'completions', hobby: '2,000/mo', pro: 'Unlimited', team: 'Unlimited', enterprise: 'Unlimited' },
    { key: 'aiAgentRequests', hobby: '50/mo', pro: '500/mo', team: '500/user/mo', enterprise: 'Unlimited' },
    { key: 'aiModels', hobby: 'GPT-4.5', pro: 'All Models', team: 'All Models', enterprise: 'All + Custom' },
    { key: 'unitTesting', hobby: false, pro: true, team: true, enterprise: true },
    { key: 'uatTesting', hobby: false, pro: false, team: true, enterprise: true },
    { key: 'sso', hobby: false, pro: false, team: true, enterprise: true },
    { key: 'adminDashboard', hobby: false, pro: false, team: true, enterprise: true },
    { key: 'prioritySupport', hobby: false, pro: true, team: true, enterprise: true },
    { key: 'dedicatedManager', hobby: false, pro: false, team: false, enterprise: true },
    { key: 'onPremise', hobby: false, pro: false, team: false, enterprise: true },
    { key: 'slaGuarantee', hobby: false, pro: false, team: false, enterprise: true },
  ];

  const faqKeys = ['tryBefore', 'exceedLimits', 'switchPlans', 'eduDiscount', 'aiModels', 'security', 'testingUnique'] as const;

  const guarantees = [
    { icon: Clock, textKey: 'freeTrial' },
    { icon: CreditCard, textKey: 'noCreditCard' },
    { icon: RefreshCw, textKey: 'cancelAnytime' },
    { icon: Lock, textKey: 'moneyBack' },
  ];

  const getPrice = (price: number | string) => {
    if (typeof price === 'string') return price;
    if (billingPeriod === 'yearly') {
      return Math.round(price * 0.8);
    }
    return price;
  };

  return (
    <>
      <SEO {...SEO_CONFIG.pricing} />
      <div className="pt-24">
      {/* Hero */}
      <section className="section-lg relative overflow-hidden">
        <PricingBackground />

        <div className="container relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-success/10 border border-success/20 text-success text-sm mb-6">
              <Gift size={16} />
              <span>{t('pricing.badge')}</span>
            </div>

            <h1 className="heading-xl mb-6">
              {t('pricing.title')}{' '}
              <span className="gradient-text">{t('pricing.titleHighlight')}</span>
            </h1>
            <p className="text-xl text-text-secondary mb-10">
              {t('pricing.subtitle')}
            </p>

            {/* Billing Toggle */}
            <div className="inline-flex items-center gap-2 p-1.5 rounded-xl bg-bg-card border border-border-default shadow-lg">
              <button
                onClick={() => setBillingPeriod('monthly')}
                className={cn(
                  'px-6 py-3 rounded-lg text-sm font-medium transition-all',
                  billingPeriod === 'monthly'
                    ? 'bg-accent text-white shadow-lg shadow-accent/25'
                    : 'text-text-secondary hover:text-text-primary'
                )}
              >
                {t('pricing.monthly')}
              </button>
              <button
                onClick={() => setBillingPeriod('yearly')}
                className={cn(
                  'px-6 py-3 rounded-lg text-sm font-medium transition-all flex items-center gap-2',
                  billingPeriod === 'yearly'
                    ? 'bg-accent text-white shadow-lg shadow-accent/25'
                    : 'text-text-secondary hover:text-text-primary'
                )}
              >
                {t('pricing.yearly')}
                <span className={cn(
                  'text-xs px-2 py-0.5 rounded-full font-semibold',
                  billingPeriod === 'yearly'
                    ? 'bg-white/20 text-white'
                    : 'bg-success/20 text-success'
                )}>
                  -20%
                </span>
              </button>
            </div>

            {/* Guarantees */}
            <div className="flex flex-wrap items-center justify-center gap-6 mt-10">
              {guarantees.map((item) => (
                <div key={item.textKey} className="flex items-center gap-2 text-sm text-text-secondary">
                  <item.icon size={16} className="text-success" />
                  {t(`pricing.guarantees.${item.textKey}`)}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="section-sm">
        <div className="container">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pricingTiers.map((tier, index) => (
              <div
                key={tier.key}
                className={cn(
                  'relative rounded-2xl border bg-bg-card p-6 flex flex-col transition-all duration-300',
                  tier.popular
                    ? 'border-accent shadow-xl shadow-accent/20 scale-105 z-10'
                    : 'border-border-default hover:border-accent/30',
                  hoveredPlan === index && !tier.popular && '-translate-y-1'
                )}
                onMouseEnter={() => setHoveredPlan(index)}
                onMouseLeave={() => setHoveredPlan(null)}
              >
                {/* Popular Badge */}
                {tier.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-accent text-white text-sm font-medium shadow-lg">
                    <Crown size={14} />
                    {t('pricing.mostPopular')}
                  </div>
                )}

                {/* Header */}
                <div className="mb-6">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${tier.gradient} flex items-center justify-center mb-4 shadow-lg`}>
                    <tier.icon className="text-white" size={24} />
                  </div>
                  <h3 className="text-xl font-bold mb-1">{tier.name}</h3>
                  <p className="text-text-tertiary text-sm">{tier.description}</p>
                </div>

                {/* Price */}
                <div className="mb-6">
                  <div className="flex items-baseline gap-2">
                    {typeof tier.price === 'number' ? (
                      <>
                        <span className="text-4xl font-bold">${getPrice(tier.price)}</span>
                        {tier.period && (
                          <span className="text-text-tertiary">/{tier.period}</span>
                        )}
                      </>
                    ) : (
                      <span className="text-4xl font-bold">{tier.price}</span>
                    )}
                  </div>
                  {billingPeriod === 'yearly' && typeof tier.price === 'number' && tier.price > 0 && (
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-text-muted line-through text-sm">
                        ${tier.originalPrice}/mo
                      </span>
                      <span className="text-success text-sm font-medium">
                        Save ${((tier.originalPrice || tier.price) - (getPrice(tier.price) as number)) * 12}/year
                      </span>
                    </div>
                  )}
                </div>

                {/* Features */}
                <ul className="space-y-3 mb-6 flex-1">
                  {tier.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-success/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check size={12} className="text-success" />
                      </div>
                      <span className="text-text-secondary text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                {tier.ctaLink.startsWith('/') ? (
                  <Link
                    to={tier.ctaLink}
                    className={cn(
                      'btn w-full justify-center',
                      tier.popular ? 'btn-primary' : 'btn-secondary'
                    )}
                  >
                    {tier.cta}
                    <ArrowRight size={18} />
                  </Link>
                ) : (
                  <a
                    href={tier.ctaLink}
                    className={cn(
                      'btn w-full justify-center',
                      tier.popular ? 'btn-primary' : 'btn-secondary'
                    )}
                  >
                    {tier.cta}
                    <ArrowRight size={18} />
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Unique Features Highlight */}
      <section className="section">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="rounded-2xl bg-gradient-to-r from-accent/10 via-purple-500/10 to-accent/10 border border-accent/20 p-8">
              <div className="text-center mb-8">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-warning/10 text-warning text-sm mb-4">
                  <Star size={14} />
                  {t('pricing.uniqueFeatures.badge')}
                </div>
                <h2 className="heading-md mb-2">{t('pricing.uniqueFeatures.title')}</h2>
                <p className="text-text-secondary">
                  {t('pricing.uniqueFeatures.subtitle')}
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex items-start gap-4 p-4 rounded-xl bg-bg-card/50 border border-border-default">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-fuchsia-600 flex items-center justify-center flex-shrink-0">
                    <FlaskConical className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">{t('pricing.comparison.unitTesting')}</h3>
                    <p className="text-text-secondary text-sm">
                      {t('pricing.uniqueFeatures.unitTestingDesc')}
                    </p>
                    <span className="text-xs text-success mt-2 inline-block">{t('pricing.uniqueFeatures.includedInPro')}</span>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 rounded-xl bg-bg-card/50 border border-border-default">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center flex-shrink-0">
                    <ClipboardCheck className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">{t('pricing.comparison.uatTesting')}</h3>
                    <p className="text-text-secondary text-sm">
                      {t('pricing.uniqueFeatures.uatTestingDesc')}
                    </p>
                    <span className="text-xs text-success mt-2 inline-block">{t('pricing.uniqueFeatures.includedInTeam')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI Models */}
      <section className="section bg-bg-surface">
        <div className="container">
          <div className="text-center mb-10">
            <h2 className="heading-lg mb-4">{t('pricing.aiModelsSection.title')}</h2>
            <p className="text-text-secondary">{t('pricing.aiModelsSection.subtitle')}</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
            {aiModels.map((model) => (
              <div key={model.name} className="text-center p-4 rounded-xl border border-border-default bg-bg-card hover:border-accent/30 transition-all group">
                <div className={`w-12 h-12 mx-auto rounded-xl bg-gradient-to-br ${model.color} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform shadow-lg`}>
                  <model.icon className="text-white" size={20} />
                </div>
                <h3 className="font-medium text-sm">{model.name}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Comparison */}
      <section className="section">
        <div className="container">
          <div className="text-center mb-10">
            <h2 className="heading-lg mb-4">{t('pricing.comparison.title')}</h2>
            <p className="text-text-secondary">{t('pricing.comparison.subtitle')}</p>
          </div>

          <div className="max-w-5xl mx-auto overflow-x-auto">
            <div className="rounded-2xl border border-border-default overflow-hidden min-w-[700px]">
              {/* Header */}
              <div className="grid grid-cols-5 bg-bg-elevated border-b border-border-default">
                <div className="p-4 font-medium text-text-secondary">{t('pricing.comparison.feature')}</div>
                <div className="p-4 text-center font-semibold">{t('pricing.tiers.hobby.name')}</div>
                <div className="p-4 text-center font-semibold bg-accent/5 border-x border-accent/20">
                  <span className="gradient-text">{t('pricing.tiers.pro.name')}</span>
                </div>
                <div className="p-4 text-center font-semibold">{t('pricing.tiers.team.name')}</div>
                <div className="p-4 text-center font-semibold">{t('pricing.tiers.enterprise.name')}</div>
              </div>

              {/* Rows */}
              {comparisonFeatures.map((row, i) => (
                <div
                  key={row.key}
                  className={cn(
                    'grid grid-cols-5',
                    i !== comparisonFeatures.length - 1 && 'border-b border-border-default',
                    'hover:bg-bg-elevated/50 transition-colors'
                  )}
                >
                  <div className="p-4 text-text-secondary">{t(`pricing.comparison.${row.key}`)}</div>
                  {['hobby', 'pro', 'team', 'enterprise'].map((plan) => {
                    const value = row[plan as keyof typeof row];
                    return (
                      <div
                        key={plan}
                        className={cn(
                          'p-4 flex items-center justify-center',
                          plan === 'pro' && 'bg-accent/5 border-x border-accent/20'
                        )}
                      >
                        {typeof value === 'boolean' ? (
                          value ? (
                            <div className="w-6 h-6 rounded-full bg-success/20 flex items-center justify-center">
                              <Check size={14} className="text-success" />
                            </div>
                          ) : (
                            <div className="w-6 h-6 rounded-full bg-bg-elevated flex items-center justify-center">
                              <X size={14} className="text-text-muted" />
                            </div>
                          )
                        ) : (
                          <span className="text-sm">{value}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section bg-bg-surface">
        <div className="container max-w-3xl">
          <div className="text-center mb-10">
            <h2 className="heading-lg mb-4">{t('pricing.faq.title')}</h2>
          </div>

          <div className="space-y-3">
            {faqKeys.map((key, index) => (
              <div
                key={key}
                className="rounded-xl border border-border-default bg-bg-card overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-bg-elevated/50 transition-colors"
                >
                  <span className="font-medium pr-4">{t(`pricing.faq.${key}.q`)}</span>
                  <ChevronDown
                    size={20}
                    className={cn(
                      'text-text-tertiary flex-shrink-0 transition-transform duration-300',
                      openFaq === index && 'rotate-180'
                    )}
                  />
                </button>
                <div
                  className={cn(
                    'overflow-hidden transition-all duration-300',
                    openFaq === index ? 'max-h-48' : 'max-h-0'
                  )}
                >
                  <p className="px-5 pb-5 text-text-secondary">{t(`pricing.faq.${key}.a`)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-lg relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-accent/10 via-transparent to-purple-500/10" />
        <div className="container relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-accent to-purple-500 flex items-center justify-center shadow-xl shadow-accent/25">
              <Shield className="text-white" size={36} />
            </div>
            <h2 className="heading-lg mb-4">
              {t('pricing.cta.title')}{' '}
              <span className="gradient-text">{t('pricing.cta.titleHighlight')}</span>
            </h2>
            <p className="text-xl text-text-secondary mb-8">
              {t('pricing.cta.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/download" className="btn btn-primary btn-lg">
                {t('pricing.cta.getStarted')}
                <ArrowRight size={20} />
              </Link>
              <Link to="/contact" className="btn btn-secondary btn-lg">
                <Headphones size={18} />
                {t('pricing.cta.talkToSales')}
              </Link>
            </div>
            <p className="text-text-tertiary text-sm mt-6">
              {t('pricing.cta.noCredit')}
            </p>
          </div>
        </div>
      </section>
    </div>
    </>
  );
}
